import React, { useState, useEffect, useCallback } from 'react';
import { WifiOff, Wifi, RefreshCw, CloudOff } from 'lucide-react';
import { getPendingCount, requestBackgroundSync } from '../utils/offlineStorage';
import { hapticWarning, hapticSuccess, IS_NATIVE } from '../utils/nativeFeatures';

/**
 * Indicateur de statut offline avec synchronisation
 * Inclut le haptic feedback pour les changements de connectivité
 */
export default function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showBanner, setShowBanner] = useState(!navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [justReconnected, setJustReconnected] = useState(false);

  const checkPending = useCallback(async () => {
    try {
      const count = await getPendingCount();
      setPendingCount(count.total);
    } catch (e) {
      // IndexedDB non disponible
    }
  }, []);

  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      setJustReconnected(true);
      setShowBanner(true);
      
      // Haptic feedback pour la reconnexion
      hapticSuccess();
      
      // Tenter la synchronisation automatique
      const count = await getPendingCount();
      if (count.total > 0) {
        setIsSyncing(true);
        await requestBackgroundSync('sync-pending-washes');
        await requestBackgroundSync('sync-pending-actions');
        
        // Attendre un peu puis vérifier
        setTimeout(async () => {
          const newCount = await getPendingCount();
          setPendingCount(newCount.total);
          setIsSyncing(false);
        }, 2000);
      }
      
      // Masquer la bannière après 3 secondes
      setTimeout(() => {
        setShowBanner(false);
        setJustReconnected(false);
      }, 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
      
      // Haptic feedback pour la perte de connexion
      hapticWarning();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Vérifier au démarrage
    checkPending();

    // Vérifier périodiquement
    const interval = setInterval(checkPending, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [checkPending]);

  const handleSync = useCallback(async () => {
    if (!isOnline || isSyncing) return;
    
    setIsSyncing(true);
    await requestBackgroundSync('sync-pending-washes');
    
    setTimeout(async () => {
      const count = await getPendingCount();
      setPendingCount(count.total);
      setIsSyncing(false);
    }, 2000);
  }, [isOnline, isSyncing]);

  // Ne rien afficher si online et pas d'actions en attente
  if (isOnline && !showBanner && pendingCount === 0) {
    return null;
  }

  return (
    <>
      {/* Bannière hors ligne */}
      {showBanner && (
        <div 
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            isOnline 
              ? 'bg-green-500' 
              : 'bg-gray-800'
          }`}
        >
          <div className="max-w-md mx-auto px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isOnline ? (
                <>
                  <Wifi className="w-4 h-4 text-white" />
                  <span className="text-white text-sm font-medium">
                    Connexion rétablie!
                  </span>
                </>  
              ) : (
                <>
                  <WifiOff className="w-4 h-4 text-white" />
                  <span className="text-white text-sm font-medium">
                    Mode hors ligne
                  </span>
                </>
              )}
            </div>
            
            {!isOnline && (
              <button
                onClick={() => setShowBanner(false)}
                className="text-white/70 hover:text-white text-sm"
              >
                Fermer
              </button>
            )}
          </div>
        </div>
      )}

      {/* Indicateur d'actions en attente */}
      {pendingCount > 0 && isOnline && (
        <div className="fixed bottom-24 right-4 z-40">
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg transition-all ${
              isSyncing 
                ? 'bg-orange-100 text-orange-600' 
                : 'bg-orange-500 text-white hover:bg-orange-600'
            }`}
          >
            <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
            <span className="text-sm font-medium">
              {isSyncing ? 'Synchronisation...' : `${pendingCount} en attente`}
            </span>
          </button>
        </div>
      )}

      {/* Indicateur compact hors ligne */}
      {!isOnline && !showBanner && (
        <div className="fixed bottom-24 right-4 z-40">
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-800 text-white rounded-full shadow-lg">
            <CloudOff className="w-4 h-4" />
            <span className="text-sm">Hors ligne</span>
          </div>
        </div>
      )}
    </>
  );
}
